







































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  Ref,
  watch,
} from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import PrettyCheck from 'pretty-checkbox-vue/check';
import { useRouter, useFormat } from '@ideavivat/vivat-2';

import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonIconTop from '@/components/CommonIconTop.vue';
import CommonChip from '@/components/CommonChip.vue';
import CommonDivider from '@/components/CommonDivider.vue';
import CommonPanelWithExpandable from '@/components/CommonPanelWithExpandable.vue';
import CommonModalWithVehicleAbsenceLogEditor from '@/components/Setting/CommonModalWithVehicleAbsenceLogEditor.vue';
import CompanyCalendar from '@/components/Setting/CompanyCalendar.vue';

import vehicleCashflow from '@/table/records/VehicleCashflow';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import useAuth from '@/useAuth';
import useCalendar from '@/useCalendar';
import fuelConsumptionTableOption from '@/table/vehicle/fuelConsumption';
import usePaymentAccount from '@/usePaymentAccount';
import { thMonthsMini } from '@/utils';

import getVehicle from '@/gql/vehicle/getVehicle.gql';
import getVehicleCashFlows from '@/gql/vehicle/getVehicleCashFlows.gql';
import getVehicleStats from '@/gql/vehicle/getVehicleStats.gql';
import getVehicleDeliveryNotesForCalendar from '@/gql/vehicle/getVehicleDeliveryNotesForCalendar.gql';
import getVehicleCashFlowStats from '@/gql/vehicle/getVehicleCashFlowStats.gql';
import getFuelConsumptions from '@/gql/vehicle/getFuelConsumptions.gql';
import createFuelConsumption from '@/gql/deliveryNote/createFuelConsumption.gql';
import getDrivers from '@/gql/driver/getDriversForSelector.gql';
import getPaymentAccounts from '@/gql/paymentAccount/getPaymentAccounts.gql';
import deleteAbsenceLog from '@/gql/driver/deleteAbsenceLog.gql';
import useDriverProfile from '@/useDriverProfile';

interface CashFlow {
  id: number;
  name: string;
  type: string;
  account: { id: string; name: string; code: string };
  amount: number;
  date: string;
}

type CashflowStatsKey = 'total' | 'fuel' | 'oil' | 'tire' | 'break' | 'electric' | 'airflow' | 'other'

const DriverInformation = defineComponent({
  components: {
    CommonModalWithPreset,
    CommonIconTop,
    CommonChip,
    CommonDivider,
    PrettyCheck,
    CommonPanelWithExpandable,
    CompanyCalendar,
    CommonModalWithVehicleAbsenceLogEditor,
  },
  setup() {
    const { me } = useAuth();
    const { route, router } = useRouter();
    const { farthestDeliveryDistanceIndex, distanceTypeToColor } = useDeliveryAreaCode();
    const {
      selectedDate,
      allowedToBooking,
      clickDateHandler,
    } = useCalendar();
    const {
      DRIVER_FEE_PAYMENT_ACCOUNT,
      DRIVER_ALLOWANCE_PAYMENT_ACCOUNT,
      DRIVER_FINE_PAYMENT_ACCOUNT,
    } = usePaymentAccount();
    const { mapCashflowStatsIntoMonth } = useDriverProfile();
    const NOW = new Date();
    const toCurrency = (str: string | number) => useFormat.number(str).currency();
    const toDecimalFormat = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Calendar Range
    const calendarStart = ref('');
    const calendarEnd = ref('');
    const calendarCurrentMonth = ref('');
    const calendarCurrentYear = ref('');
    function handleDatesRender(event: any) {
      calendarStart.value = useFormat.date(event.view.activeStart).computerShortDate();
      calendarEnd.value = useFormat.date(event.view.activeEnd).computerShortDate();
      calendarCurrentMonth.value = (event.view.currentStart.getMonth() + 1);
      calendarCurrentYear.value = (event.view.currentStart.getFullYear());
    }

    const filterDate = computed(() => ({
      month: calendarCurrentMonth.value,
      year: calendarCurrentYear.value,
    }));

    const defaultStats = {
      tripsCount: 0, totalDistance: 0, percentWithTrip: 0, percentAbsent: 0, percentRest: 0, expensePerKm: 0, fuelConsumption: 0, fuelConsumptionPerKm: 0,
    };
    const { result: getVehicleStatsResult } = useQuery(getVehicleStats, computed(() => ({
      id: route.value.params.id,
      year: filterDate.value.year || undefined,
      month: filterDate.value.month || undefined,
    })), () => ({ fetchPolicy: 'network-only' }));
    const averageVehicleStats = useResult(getVehicleStatsResult, defaultStats, (data) => data.averageVehicleStats);
    const vehicleStats = useResult(getVehicleStatsResult, defaultStats, (data) => data.vehicle.stats);

    const defaultVehicle = {
      license: '',
      province: '',
      model: '',
      color: '',
      year: '',
      cashFlows: [],
    };
    const vehicle: Ref<any> = ref(defaultVehicle);
    const { result: getVehicleResult, loading } = useQuery(getVehicle, computed(() => ({
      id: route.value.params.id,
    })), () => ({ fetchPolicy: 'network-only' }));
    const vehicleResult = useResult(getVehicleResult, defaultVehicle, (data) => data.vehicle);
    watch(() => vehicleResult.value, (newVal, oldVal) => {
      if (!loading.value && newVal !== oldVal) {
        vehicle.value = {
          ...vehicleResult.value,
        };
      }
    });

    const filterCashflowTable = reactive({ searchMaintenance: '', searchOther: '' });
    const getMaintenanceCashflowVariables = computed(() => {
      const vars: {[key: string]: any} = { id: route.value.params.id, filterPaymentAccount: undefined };
      if (filterCashflowTable.searchMaintenance) { vars.filterPaymentAccount = parseInt((filterCashflowTable.searchMaintenance as any).id, 10); }
      return vars;
    });
    const getOtherCashflowVariables = computed(() => {
      const vars: {[key: string]: any} = { id: route.value.params.id, filterPaymentAccount: undefined };
      if (filterCashflowTable.searchOther) { vars.filterPaymentAccount = `%${filterCashflowTable.searchOther}%`; }
      return vars;
    });

    const selectedMaintenanceCashflowItemId: Ref<number[]> = ref([]);
    function selectMaintenanceCashflowItem(id: number) {
      const hitIndex = selectedMaintenanceCashflowItemId.value.indexOf(id);
      if (hitIndex > -1) {
        selectedMaintenanceCashflowItemId.value.splice(hitIndex, 1);
      } else {
        selectedMaintenanceCashflowItemId.value.push(id);
      }
    }
    const selectedCashflowItemId: Ref<number[]> = ref([]);
    function selectCashflowItem(id: number) {
      const hitIndex = selectedCashflowItemId.value.indexOf(id);
      if (hitIndex > -1) {
        selectedCashflowItemId.value.splice(hitIndex, 1);
      } else {
        selectedCashflowItemId.value.push(id);
      }
    }

    // For Chart
    const selectedChartYear = ref(new Date().getFullYear() + 543);

    const cashflowStatsKey: CashflowStatsKey[] = ['total', 'fuel', 'oil', 'tire', 'break', 'electric', 'airflow', 'other'];
    const cashflowStatsKeyThai = ['ค่าใช้จ่ายประจำปี', 'ค่าน้ำมันเชื้อเพลิง', 'ถ่ายน้ำมันเครื่อง', 'ยาง', 'ระบบเบรค', 'ระบบไฟ', 'ระบบแอร์', 'อื่น ๆ'];
    const DEFAULT_CASHFLOW_STATS = () => {
      const result = {};
      cashflowStatsKey.forEach((key: string) => {
        (result as any)[key] = {
          total: 0,
          months: [],
        } as any;
      });
      return result;
    };
    const vehicleCashFlowStats: Ref<any> = ref(DEFAULT_CASHFLOW_STATS());
    const { result: getVehicleCashFlowStatsResult, loading: getVehicleCashFlowStatsLoading } = useQuery(getVehicleCashFlowStats, computed(() => ({
      id: route.value.params.id,
      year: selectedChartYear.value - 543,
      fuelPAymentAccountId: 7,
      mechanicOilPaymentAccountId: 9,
      tirePaymentAccountId: 10,
      breakPaymentAccountId: 11,
      electricSystemPaymentAccountId: 12,
      airflowSystemPaymentAccountId: 13,
      otherPaymentAccountId: 14,
    })), () => ({ fetchPolicy: 'network-only' }));
    const vehicleCashFlowStatsResult = useResult(getVehicleCashFlowStatsResult, DEFAULT_CASHFLOW_STATS(), (data) => data.vehicle);

    watch(() => vehicleCashFlowStatsResult.value, (newVal, oldVal) => {
      if (!getVehicleCashFlowStatsLoading.value && newVal !== oldVal) {
        vehicleCashFlowStats.value = vehicleCashFlowStatsResult.value;
      }
    });
    const totalExpense = computed(() => useFormat.number(vehicleCashFlowStats.value[cashflowStatsKey[0]].total).currency());
    const backwardTenYear = Array.from({ length: 10 }, (x, i) => new Date().getFullYear() + 543 - i);
    const computedCashflowData = computed(() => {
      const result: { name: string; data: { x: string; y: number }[] }[] = [];
      cashflowStatsKey.forEach((key: string, index: number) => {
        result.push({ name: cashflowStatsKeyThai[index], data: [] });
        if (vehicleCashFlowStats.value[key].months.length !== 0) {
          result[result.length - 1].data = vehicleCashFlowStats.value[key].months.map(mapCashflowStatsIntoMonth);
        }
      });
      return result;
    });

    const areaCharts = computed(() => ({
      options: {
        dataLabels: { enabled: false },
        yaxis: { opposite: false },
      },
      series: computedCashflowData.value,
    }));

    // For Calendar
    // function navigateToDeliveryNotesDate(date: Date) {
    //   router.push({
    //     name: 'DeliveryNoteList',
    //     params: {
    //       date: useFormat.date(date).computerShortDate(),
    //     },
    //   });
    // }
    const absenceEditModal = ref(null);
    const selectedAbsenceLog = ref({ id: -1 });
    function clickEventHandler(event: { item: {[key: string]: any}; index: number }) {
      if (event.item.type === 'deliveryNote') {
        router.push({
          name: 'DeliveryNoteInformation',
          params: {
            id: event.item.id,
          },
        });
      } else if (event.item.type === 'absenceLog') {
        selectedAbsenceLog.value = { id: event.item.id };
      }
    }
    const filterCalendarDueDate = computed(() => ({
      from: `${calendarStart.value}`,
      to: `${calendarEnd.value}`,
    }));
    const { result: calendarQueryResult } = useQuery(getVehicleDeliveryNotesForCalendar, computed(() => ({
      id: route.value.params.id,
      filterCreatedAtBetween: filterCalendarDueDate.value.from ? filterCalendarDueDate.value : undefined,
    })), {
      fetchPolicy: 'cache-and-network',
    });
    const vehicleCalendarInformation = useResult(calendarQueryResult, { deliveryNotes: [], cashFlows: [], absenceLogs: [] }, (data) => data.vehicle);
    const events = computed(() => {
      const result = [...vehicleCalendarInformation.value.deliveryNotes as any[]].map((elm) => {
        const color = distanceTypeToColor(elm.distanceType);
        const distanceArr: any[] = elm.deliveries.map((delivery: any) => delivery?.area?.distanceType || '');
        const farthestIndex = farthestDeliveryDistanceIndex(distanceArr);
        return {
          id: elm.id,
          accountId: '',
          amount: 0,
          title: `${farthestIndex !== null ? `[${elm.deliveries[farthestIndex]?.area?.code || ''}] ` : ''}${elm.uniqueId}`,
          start: useFormat.date(elm.deliveryDate).computerShortDate(),
          type: 'deliveryNote',
          color,
        };
      });

      const absences = vehicleCalendarInformation.value.absenceLogs?.map((elm: any) => ({
        id: elm.id,
        allDay: true,
        title: `[พักงาน] ${elm.reason}`,
        start: useFormat.date(elm.effectiveDate).computerShortDate(),
        end: useFormat.date(elm.leaveUntil).computerShortDate(),
        type: 'absenceLog',
        color: 'orange',
      }));
      result.push(...absences);

      if (me.company) {
        vehicleCalendarInformation.value.cashFlows.forEach((cashFlow: CashFlow) => {
          const hitIndex = result.findIndex((cashFlowElm: any) => cashFlowElm.accountId === cashFlow.account.id
            && cashFlowElm.start === cashFlow.date);

          let color = '';
          if (parseInt(cashFlow.account.id, 10) === DRIVER_FINE_PAYMENT_ACCOUNT) color = 'orange';
          else if (parseInt(cashFlow.account.id, 10) === DRIVER_FEE_PAYMENT_ACCOUNT) color = 'pink';
          else if (parseInt(cashFlow.account.id, 10) === DRIVER_ALLOWANCE_PAYMENT_ACCOUNT) color = 'yellow';
          if (hitIndex > -1) {
            result[hitIndex].amount += cashFlow.amount;
            result[hitIndex].title = `${toCurrency(result[hitIndex].amount)} - ${cashFlow.name}`;
          } else {
            result.push({
              id: cashFlow.id,
              accountId: cashFlow.account.id,
              amount: cashFlow.amount,
              title: `${toCurrency(cashFlow.amount)} - ${cashFlow.name}`,
              start: cashFlow.date,
              type: 'cashFlow',
              color,
            });
          }
        });
      }

      return result;
    });

    const absenceDeleteModal = ref(null);
    const deleteAbsenceLogMutation = useMutation(deleteAbsenceLog, () => ({
      refetchQueries: ['getDataForVehicleCalendar'],
    }));
    deleteAbsenceLogMutation.onDone(() => {
      selectedAbsenceLog.value = { id: -1 };
    });
    function confirmDeleteAbsenceLog(ctx: any) {
      deleteAbsenceLogMutation.mutate({
        id: ctx.id,
      });
    }

    return {
      me,
      toDecimalFormat,
      vehicleStats,
      averageVehicleStats,
      getPaymentAccounts,

      absenceEditModal,
      absenceDeleteModal,
      selectedAbsenceLog,
      confirmDeleteAbsenceLog,

      vehicle,

      getVehicleCashFlows,
      vehicleCashflowTableOption: vehicleCashflow,
      getVehicleCashFlowsResult: (data: any) => data.vehicle.paginatedCashFlows,
      filterCashflowTable,

      selectMaintenanceCashflowItem,
      getMaintenanceCashflowVariables,
      selectedMaintenanceCashflowItemId,

      selectCashflowItem,
      getOtherCashflowVariables,
      selectedCashflowItemId,

      // For chart
      backwardTenYear,
      totalExpense,
      selectedChartYear,
      areaCharts,

      // For calendar
      events,
      handleDatesRender,
      // navigateToDeliveryNotesDate,
      clickEventHandler,

      // For FuelConsumption
      getFuelConsumptions,
      fuelConsumptionTableOption,
      getFuelConsumptionResult: (data: any) => data.deliveryNotes,

      getDrivers,
      getDriversResult: (data: any) => data.drivers.data,

      selectedDate,
      allowedToBooking,
      clickDateHandler,
    };
  },
});

export default DriverInformation;
